import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import OrganizerLayout from '../components/OrganizerLayout';

interface Domain {
  id: string;
  name: string;
}

interface ProblemStatement {
  id: string;
  title: string;
  description: string | null;
  domain_id: string | null;
  difficulty: string | null;
  is_published: boolean;
  is_enabled: boolean;
  created_at: string;
  domain_name?: string;
}

const difficultyColor = (d: string) => {
  switch (d) {
    case 'Hard': return 'bg-red-100 text-red-700';
    case 'Medium': return 'bg-amber-100 text-amber-700';
    case 'Easy': return 'bg-accent-100 text-accent-700';
    default: return 'bg-background-200 text-foreground-500';
  }
};

export default function OrganizerProblemStatements() {
  const { profile } = useAuth();
  const [statements, setStatements] = useState<ProblemStatement[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', domain_id: '', difficulty: 'Medium', description: '', doctor_name: '', hospital_name: '' });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [psRes, domRes] = await Promise.all([
        supabase.from('problem_statements').select('*').order('created_at', { ascending: false }),
        supabase.from('domains').select('id, name').order('name'),
      ]);
      if (psRes.error) throw psRes.error;
      if (domRes.error) throw domRes.error;

      const domainsMap: Record<string, string> = {};
      (domRes.data || []).forEach((d) => { domainsMap[d.id] = d.name; });

      setStatements((psRes.data || []).map((ps) => ({
        ...ps,
        domain_name: ps.domain_id ? (domainsMap[ps.domain_id] || 'Unknown') : 'Unassigned',
      })));
      setDomains(domRes.data || []);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useRealtime<ProblemStatement>({ table: 'problem_statements', onAny: () => loadData() });

  const filtered = statements.filter((ps) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return ps.title.toLowerCase().includes(q) || (ps.domain_name || '').toLowerCase().includes(q);
  });

  const togglePublish = async (id: string, current: boolean) => {
    try { await supabase.from('problem_statements').update({ is_published: !current }).eq('id', id); } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed'); }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.domain_id) return;
    setSaving(true);
    try {
      if (editId) {
        await supabase.from('problem_statements').update({
          title: form.title.trim(), domain_id: form.domain_id, difficulty: form.difficulty, description: form.description.trim(),
          doctor_name: form.doctor_name.trim(), hospital_name: form.hospital_name.trim(),
          updated_at: new Date().toISOString(),
        }).eq('id', editId);
      } else {
        await supabase.from('problem_statements').insert({
          title: form.title.trim(), domain_id: form.domain_id, difficulty: form.difficulty, description: form.description.trim(),
          doctor_name: form.doctor_name.trim(), hospital_name: form.hospital_name.trim(),
          is_published: false, is_enabled: true, created_by: profile?.id,
        });
      }
      setShowModal(false);
      setEditId(null);
      setForm({ title: '', domain_id: '', difficulty: 'Medium', description: '', doctor_name: '', hospital_name: '' });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const openEdit = (ps: ProblemStatement) => {
    setEditId(ps.id);
    setForm({ title: ps.title, domain_id: ps.domain_id || '', difficulty: ps.difficulty || 'Medium', description: ps.description || '', doctor_name: (ps as Record<string, unknown>).doctor_name as string || '', hospital_name: (ps as Record<string, unknown>).hospital_name as string || '' });
    setShowModal(true);
  };

  return (
    <AuthGuard requiredRole="organizer">
      <OrganizerLayout title="Problem Statements" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/organizer' }, { label: 'Problem Statements' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="flex items-center justify-between mb-5">
          <div className="relative w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search problem statements..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none transition-colors" />
          </div>
          <button onClick={() => { setEditId(null); setForm({ title: '', domain_id: '', difficulty: 'Medium', description: '', doctor_name: '', hospital_name: '' }); setShowModal(true); }} className="px-4 py-2.5 rounded-lg bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line"></i> Add Statement
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-secondary-500 border-t-transparent animate-spin"></div></div>
        ) : statements.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-question-answer-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No problem statements available</p>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['Title', 'Domain', 'Difficulty', 'Published', 'Date', 'Actions'].map((l) => (
                      <th key={l} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ps) => (
                    <tr key={ps.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-900 max-w-xs truncate">{ps.title}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap text-xs">{ps.domain_name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${difficultyColor(ps.difficulty || '')}`}>{ps.difficulty || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => togglePublish(ps.id, ps.is_published)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${ps.is_published ? 'bg-accent-500' : 'bg-background-300'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${ps.is_published ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">{new Date(ps.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(ps)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary-50 text-foreground-400 hover:text-secondary-600 transition-colors cursor-pointer" title="Edit">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-foreground-400">No problem statements found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit' : 'Add'} Problem Statement</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Title</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="Enter title..." />
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Domain</label>
                  <select value={form.domain_id} onChange={(e) => setForm({ ...form, domain_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-secondary-400 outline-none cursor-pointer">
                    <option value="">Select a domain...</option>
                    {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Difficulty</label>
                  <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-secondary-400 outline-none cursor-pointer">
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none resize-none" placeholder="Enter description..."></textarea>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Doctor Name</label>
                    <input type="text" value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="e.g. Dr. Ramesh Kumar" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Hospital Name</label>
                    <input type="text" value={form.hospital_name} onChange={(e) => setForm({ ...form, hospital_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-secondary-400 outline-none" placeholder="e.g. ABC Multispeciality Hospital" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-lg bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Statement'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </OrganizerLayout>
    </AuthGuard>
  );
}